import type { AuthSession } from '@logistech/ui-shared';

import type { Permission } from '@/lib/permissions';

type Role = AuthSession['user']['role'];

export const ROLE_LABELS: Record<Role, string> = {
  OWNER: 'Propriétaire',
  ADMIN: 'Administrateur',
  MANAGER: 'Responsable',
  DISPATCHER: 'Dispatcher',
  DRIVER: 'Chauffeur',
  ACCOUNTANT: 'Comptable',
  VIEWER: 'Observateur',
  API_PARTNER: 'Partenaire API',
};

// Classes Tailwind à passer à <Badge className=…> ; violet/teal = couleurs de marque (pas de token tailwind)
export const ROLE_COLORS: Record<Role, string> = {
  OWNER: 'border-transparent bg-brand-violet text-white',
  ADMIN: 'border-transparent bg-brand-teal text-white',
  MANAGER: 'border-transparent bg-blue-600 text-white',
  DISPATCHER: 'border-transparent bg-orange-500 text-white',
  DRIVER: 'border-transparent bg-green-600 text-white',
  ACCOUNTANT: 'border-transparent bg-slate-600 text-white',
  VIEWER: 'border-transparent bg-zinc-400 text-white',
  API_PARTNER: 'border-transparent bg-neutral-900 text-white',
};

export const getInitials = (firstName?: string, lastName?: string): string =>
  `${firstName?.trim()[0] ?? ''}${lastName?.trim()[0] ?? ''}`.toUpperCase() || '?';

// « 0192a3b4-…-9f8e7d6cabcd » → « XXXX-...-abcd »
export const maskId = (id: string): string => `XXXX-...-${id.slice(-4)}`;

// Libellés de la matrice pédagogique (/team, onglet Permissions)
export const PERMISSION_LABELS: Record<Permission, string> = {
  'tenants.read': 'Voir les informations du tenant',
  'tenants.update': 'Modifier le tenant et son branding',
  'users.read': 'Voir les membres',
  'users.invite': 'Inviter des membres',
  'users.changeRole': 'Changer le rôle d’un membre',
  'users.delete': 'Retirer un membre',
  'users.me': 'Gérer son propre profil',
  'vehicles.read': 'Voir les véhicules',
  'vehicles.write': 'Créer et modifier les véhicules',
  'vehicles.delete': 'Supprimer des véhicules',
  'drivers.read': 'Voir les conducteurs',
  'drivers.write': 'Créer et modifier les conducteurs',
  'drivers.delete': 'Supprimer des conducteurs',
  'missions.read': 'Voir les missions',
  'missions.write': 'Créer et modifier les missions',
  'missions.delete': 'Supprimer des missions',
  'rotations.delete': 'Supprimer des rotations',
  'alerts.read': 'Voir les alertes',
  'alerts.write': 'Traiter les alertes',
  'reports.read': 'Voir les rapports',
  'reports.generate': 'Générer des rapports',
  'dashboard.read_activity': 'Voir l’activité par client du tableau de bord',
  'billing.read': 'Voir l’abonnement',
  'billing.write': 'Gérer l’abonnement',
  'invoices.read': 'Voir les factures clients',
  'invoices.write': 'Émettre et encaisser les factures clients',
  'maintenance.read': 'Voir la maintenance',
  'maintenance.write': 'Planifier la maintenance',
  'fuel.read': 'Voir le carburant',
  'fuel.write': 'Saisir les pleins',
  'geofences.read': 'Voir les géofences',
  'geofences.write': 'Créer et modifier les géofences',
  'notifications.read': 'Voir les notifications',
  'integrations.read': 'Voir les clés API et webhooks',
  'integrations.write': 'Gérer les clés API et webhooks',
  'truck_owners.read_financial': 'Voir les coordonnées bancaires et fiscales des propriétaires de camions',
};

// Chaque permission apparaît dans exactement un domaine (vérifié par le test de la matrice)
export const PERMISSION_GROUPS: { label: string; permissions: Permission[] }[] = [
  { label: 'Missions', permissions: ['missions.read', 'missions.write', 'missions.delete', 'rotations.delete'] },
  {
    label: 'Flotte',
    permissions: [
      'vehicles.read', 'vehicles.write', 'vehicles.delete', 'drivers.read', 'drivers.write', 'drivers.delete',
      'fuel.read', 'fuel.write', 'geofences.read', 'geofences.write',
    ],
  },
  { label: 'Propriétaires de camions', permissions: ['truck_owners.read_financial'] },
  { label: 'Factures', permissions: ['invoices.read', 'invoices.write', 'billing.read', 'billing.write'] },
  { label: 'Maintenance', permissions: ['maintenance.read', 'maintenance.write'] },
  { label: 'Alertes', permissions: ['alerts.read', 'alerts.write', 'notifications.read'] },
  { label: 'Rapports', permissions: ['reports.read', 'reports.generate', 'dashboard.read_activity'] },
  {
    label: 'Utilisateurs',
    permissions: ['users.read', 'users.invite', 'users.changeRole', 'users.delete', 'users.me'],
  },
  { label: 'Tenant', permissions: ['tenants.read', 'tenants.update'] },
  { label: 'Intégrations', permissions: ['integrations.read', 'integrations.write'] },
];
